import { useState, useEffect, useContext, useCallback, ReactNode } from "react";
import {
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  CircularProgress,
  MenuItem,
  InputAdornment,
  IconButton,
  Typography,
  Link,
  Alert
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useOrderInfo } from "../shared/orderInfo/OrderInfoContext";
import LedgerSummary from "./LedgerSummary";
import LedgerLoader from "./orderInfo/LedgerLoader";
import Iconify from "../../minimals/components/iconify";
import { UserContext } from "../contexts/UserContext";
import { UploadMultiFile } from "../components/upload";
import { stripBase64LeadingChars, toBase64 } from "../utils/helpers";
import { checkIfLedgerExists } from "../shared/requests/ledgers";
import { fCurrency } from "../utils/formatNumber";

type RecordingLedgerInfo = {
  hasUncategorizedTransfers: boolean;
  buyerRecordingFees: number | null;
  sellerRecordingFees: number | null;
  buyerTransferTax: number | null;
  sellerTransferTax: number | null;
  recordingFees: number | null;
  transferTax: number | null;
  mortgageTax: number | null;
  withholdingTax: number | null;
};

type LedgerTransaction = {
  id: string;
  status: string;
  amount: number;
  name: string;
  payorPayee: string;
  type: string;
  typeDescription: string;
  isDebit: boolean;
  referenceNumber: string;
  memo: string | null;
  systemPostedOn: string | null;
  userPostedOn: string | null;
  voidedOn: string | null;
  clearedOn: string | null;
  transactionDate: string | null;
};

type TransactionId = string | null;

const existingAmount = (amount: number | null | undefined) => (amount && amount > 0 ? amount.toString() : "");

const ClosingChecklistLedgers = ({ hideLedger, refresh, escrowContent, stackLedgers }: {
  hideLedger?: boolean;
  refresh?: () => any;
  escrowContent?: ReactNode;
  stackLedgers?: boolean;
}) => {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get("order");
  const { user } = useContext(UserContext);
  const orderInfo = useOrderInfo();

  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [hasOrderLedger, setHasOrderLedger] = useState(false);
  const [buyerRecordingFees, setBuyerRecordingFees] = useState("");
  const [sellerRecordingFees, setSellerRecordingFees] = useState("");
  const [buyerTransferTax, setBuyerTransferTax] = useState("");
  const [sellerTransferTax, setSellerTransferTax] = useState("");
  const [mortgageTax, setMortgageTax] = useState("");
  const [withholdingTax, setWithholdingTax] = useState("");
  const [recordingModal, setRecordingModal] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [transactions, setTransactions] = useState<LedgerTransaction[]>([]);

  const [buyerRecordingFeeTransaction, setBuyerRecordingFeeTransaction] = useState<TransactionId>(null);
  const [sellerRecordingFeeTransaction, setSellerRecordingFeeTransaction] = useState<TransactionId>(null);
  const [buyerTransferTaxTransaction, setBuyerTransferTaxTransaction] = useState<TransactionId>(null);
  const [sellerTransferTaxTransaction, setSellerTransferTaxTransaction] = useState<TransactionId>(null);
  const [mortgageTaxTransaction, setMortgageTaxTransaction] = useState<TransactionId>(null);
  const [withholdingTaxTransaction, setWithholdingTaxTransaction] = useState<TransactionId>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [recordingLedgerInfo, setRecordingLedgerInfo] = useState<RecordingLedgerInfo | null>(null);

  const [isRecordingTransferInProgress, setIsRecordingTransferInProgress] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleFileDrop = useCallback(
    (acceptedFile: any) => {
      const file = Object.assign(acceptedFile[0], {
        preview: URL.createObjectURL(acceptedFile[0])
      });
      setFile(file);
    },
    [setFile]
  );

  const loadExistingRecordingLedger = async () => {
    const { data } = await axios.get(`/proxy/api/fundstransfer/GetRecordingTransactions?fileNumber=${orderNumber}`);
    setIsLoading(false);
    setRecordingLedgerInfo(data);
  };

  const getLedgerBalance = async () => {
    const isLedgerFound = await checkIfLedgerExists(orderNumber);
    if (!isLedgerFound) {
      setHasOrderLedger(false);
      return;
    }

    const { data } = await axios.get(`/proxy/api/ledgers/GetOrderAndLedgerBalance?ordernumber=${orderNumber}`);
    setLedgerBalance(data.ledgerBalance);
    setHasOrderLedger(true);
  };

  const loadTransactions = async () => {
    const response = await axios.get<LedgerTransaction[]>(
      `/proxy/api/ledgers/gettransactions?orderNumber=${orderNumber}`
    );
    const filteredTransactions = response.data.filter((transaction) => {
      return transaction.typeDescription.startsWith("Ledger Transfer") && transaction.status === "Pending";
    });
    setTransactions(filteredTransactions);
  };

  useEffect(() => {
    loadExistingRecordingLedger();
    loadTransactions();
    getLedgerBalance();
  }, []);

  const getRecordingLedgerUI = () => {
    const { recordingFees, transferTax, mortgageTax, withholdingTax } = recordingLedgerInfo
      ? recordingLedgerInfo
      : ({} as RecordingLedgerInfo);
    const total = [recordingFees, transferTax, mortgageTax, withholdingTax]
      .filter((f) => Boolean(f))
      .reduce((prev, curr) => prev! + curr!, 0);

    const transactions = [
      { name: "Recording Fees", amount: recordingFees },
      { name: "Transfer Tax", amount: transferTax },
      { name: "Mortgage Tax", amount: mortgageTax },
      { name: "Withholding Tax", amount: withholdingTax }
    ] as LedgerTransaction[];

    return (
      <>
        <Button
          variant="outlined"
          onClick={() => {
            setBuyerRecordingFees(existingAmount(recordingLedgerInfo?.buyerRecordingFees));
            setSellerRecordingFees(existingAmount(recordingLedgerInfo?.sellerRecordingFees));
            setBuyerTransferTax(existingAmount(recordingLedgerInfo?.buyerTransferTax));
            setSellerTransferTax(existingAmount(recordingLedgerInfo?.sellerTransferTax));
            setMortgageTax(existingAmount(recordingLedgerInfo?.mortgageTax));
            setWithholdingTax(existingAmount(recordingLedgerInfo?.withholdingTax));
            setBuyerRecordingFeeTransaction(null); setSellerRecordingFeeTransaction(null);
            setBuyerTransferTaxTransaction(null); setSellerTransferTaxTransaction(null);
            setMortgageTaxTransaction(null); setWithholdingTaxTransaction(null);
            setTransferError("");
            setRecordingModal(true);
          }}
          size="small"
          disabled={isLoading || !!recordingLedgerInfo?.hasUncategorizedTransfers}
          startIcon={recordingLedgerInfo ? <Iconify icon="eva:edit-2-outline" /> : undefined}
          sx={{ fontSize: { sm: "0.68rem", lg: "0.875rem" }, whiteSpace: "nowrap" }}
        >
          {recordingLedgerInfo ? "Edit Recording Fees" : "Transfer Recording Fees"}
        </Button>
        {!isLoading && !hideLedger && recordingLedgerInfo && (
          <LedgerSummary
            ledgerTypeHeader={"Recording Ledgers"}
            total={total!}
            transactions={transactions}
            orderNumber={orderNumber!!}
            isRecording
          />
        )}
        {isLoading && !hideLedger && <LedgerLoader />}
      </>
    );
  };

  const uploadFiles = async () => {
    if (!file) return;
    const recordingFiles = [{ name: file.name, base64Contents: stripBase64LeadingChars(await toBase64(file as File)) }];
    await axios.post("/api/file/uploadfiles", { orderNumber, recordingFiles });
  };

  const onRecordingModalTransferClick = async () => {
    setIsRecordingTransferInProgress(true);
    setTransferError("");
    try {
    await axios.post("/proxy/api/fundstransfer/TransferToRecordingLedger", {
      fileNumber: orderNumber,
      checkRecordingState: true,
      expectedRecordingState: recordingLedgerInfo,
      buyerRecordingFees: buyerRecordingFees === "" ? null : Number(buyerRecordingFees),
      sellerRecordingFees: sellerRecordingFees === "" ? null : Number(sellerRecordingFees),
      buyerTransferTax: buyerTransferTax === "" ? null : Number(buyerTransferTax),
      sellerTransferTax: sellerTransferTax === "" ? null : Number(sellerTransferTax),
      mortgageTax: mortgageTax === "" ? null : Number(mortgageTax),
      withholdingTax: withholdingTax === "" ? null : Number(withholdingTax),
      userName: user?.username,
      buyerRecordingFeeTransactionId: buyerRecordingFeeTransaction,
      sellerRecordingFeeTransactionId: sellerRecordingFeeTransaction,
      buyerTransferTaxTransactionId: buyerTransferTaxTransaction,
      sellerTransferTaxTransactionId: sellerTransferTaxTransaction,
      mortgageTaxTransactionId: mortgageTaxTransaction,
      withholdingTaxTransactionId: withholdingTaxTransaction
    });
    setRecordingModal(false);
    setUploadError("");
    const [recordingResult, transactionsResult, balanceResult, parentRefreshResult, uploadResult] = await Promise.allSettled([
      loadExistingRecordingLedger(),
      loadTransactions(),
      getLedgerBalance(),
      Promise.resolve().then(() => refresh?.()),
      file ? uploadFiles() : Promise.resolve()
    ]);
    const messages: string[] = [];
    if ([recordingResult, transactionsResult, balanceResult, parentRefreshResult].some((result) => result.status === "rejected")) {
      messages.push("Recording fees were saved, but some information could not be refreshed. Reload the page.");
    }
    if (uploadResult.status === "rejected") {
      messages.push("Recording fees were saved, but the document could not be uploaded.");
    } else {
      setFile(null);
    }
    setUploadError(messages.join(" "));
    } catch (error) {
      setTransferError(axios.isAxiosError(error) ? (error.response?.data?.exceptionMessage || error.response?.data?.message || "Unable to update recording fees. Please try again.") : "Unable to update recording fees.");
    } finally {
      setIsRecordingTransferInProgress(false);
    }
  };

  const onRecordingModalCancelClick = () => {
    setRecordingModal(false);
  };

  const shouldShowWithholdingTax = orderInfo?.properties?.some((p) => ["NJ", "MD", "DE"].some((s) => p.state === s));

  const selectedTransactions = [
    buyerRecordingFeeTransaction,
    sellerRecordingFeeTransaction,
    withholdingTaxTransaction,
    mortgageTaxTransaction,
    buyerTransferTaxTransaction,
    sellerTransferTaxTransaction
  ];
  const unselectedTransactions = transactions.filter((t) => !selectedTransactions.includes(t.id));

  const entries = [buyerRecordingFees, sellerRecordingFees, buyerTransferTax, sellerTransferTax, mortgageTax, withholdingTax];
  const existingAmounts = [recordingLedgerInfo?.buyerRecordingFees, recordingLedgerInfo?.sellerRecordingFees,
    recordingLedgerInfo?.buyerTransferTax, recordingLedgerInfo?.sellerTransferTax,
    recordingLedgerInfo?.mortgageTax, recordingLedgerInfo?.withholdingTax];
  const validEntries = entries.every((value, index) => value === "" ? !existingAmounts[index] : /^(?:\d+)(?:\.\d{1,2})?$/.test(String(value)) && Number(value) > 0);
  const netIncrease = entries.reduce((sum, value, index) => sum + (value === "" ? 0 : Number(value) - (existingAmounts[index] || 0)), 0);
  const isValidModalAmount = validEntries && entries.some(Boolean) && netIncrease <= ledgerBalance;

  return (
    <>
      {uploadError && <Typography color="error">{uploadError}</Typography>}
      <Stack direction="column" spacing={1} maxWidth={escrowContent ? undefined : 400}>
        {recordingLedgerInfo?.hasUncategorizedTransfers && (
          <Alert severity="warning">
            An existing transfer has no recognized recording category. Review the{" "}
            <Link href={`/ledger/${encodeURIComponent(`${orderNumber}-RECORDING`)}`} target="_blank" rel="noopener noreferrer">
              recording ledger
            </Link>{" "}
            before editing these fees.
          </Alert>
        )}
        {!hideLedger && hasOrderLedger && (
          <Link href={`/ledger/${encodeURIComponent(orderNumber!)}`} target="_blank" rel="noopener noreferrer">
            Order Ledger: {fCurrency(ledgerBalance)}
          </Link>
        )}
        <Stack direction={stackLedgers ? "column" : "row"} spacing={2} alignItems="flex-start" useFlexGap flexWrap="wrap">
          <Stack spacing={1} alignItems="flex-start">
            {getRecordingLedgerUI()}
          </Stack>
          {escrowContent}
        </Stack>
      </Stack>
      <Dialog open={recordingModal} maxWidth="sm" fullWidth>
        <DialogTitle>{recordingLedgerInfo ? "Edit Recording Fees" : "Transfer Funds to Recording"}</DialogTitle>
        {recordingLedgerInfo && <Typography variant="body2" sx={{ ml: 2 }}>Enter the desired total for each category.</Typography>}
        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
          Current Ledger Balance: {ledgerBalance?.toFixed(2)}
        </Typography>
        <DialogContent sx={{ paddingBottom: 4 }}>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  disabled={Boolean(buyerRecordingFeeTransaction)}
                  fullWidth
                  label="Buyer Recording Fees"
                  type="number"
                  value={buyerRecordingFees}
                  onChange={(e) => setBuyerRecordingFees(e.target.value)}
                  InputProps={{
                    startAdornment: "$"
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Choose Pending Transaction"
                  disabled={Boolean(recordingLedgerInfo?.buyerRecordingFees)}
                  value={buyerRecordingFeeTransaction}
                  onChange={(e) => {
                    const transaction = transactions.find((t) => t.id === e.target.value);
                    if (transaction) {
                      setBuyerRecordingFees(transaction?.amount.toString());
                      setBuyerRecordingFeeTransaction(transaction.id);
                    }
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: buyerRecordingFeeTransaction && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setBuyerRecordingFeeTransaction(null);
                            setBuyerRecordingFees("");
                          }}
                          size="small"
                        >
                          <Iconify icon="material-symbols:close-small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                >
                  {[...unselectedTransactions, transactions.find((t) => t.id === buyerRecordingFeeTransaction)]
                    .filter((a) => Boolean(a))
                    .map((transaction) => (
                      <MenuItem key={transaction!.id} value={transaction!.id}>
                        {transaction!.name} - ${transaction!.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  disabled={Boolean(sellerRecordingFeeTransaction)}
                  fullWidth
                  label="Seller Recording Fees"
                  type="number"
                  value={sellerRecordingFees}
                  onChange={(e) => setSellerRecordingFees(e.target.value)}
                  InputProps={{
                    startAdornment: "$"
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Choose Pending Transaction"
                  disabled={Boolean(recordingLedgerInfo?.sellerRecordingFees)}
                  value={sellerRecordingFeeTransaction}
                  onChange={(e) => {
                    const transaction = transactions.find((t) => t.id === e.target.value);
                    setSellerRecordingFees(transaction?.amount as any);
                    setSellerRecordingFeeTransaction(e.target.value);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: sellerRecordingFeeTransaction && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setSellerRecordingFeeTransaction(null);
                            setSellerRecordingFees("");
                          }}
                          size="small"
                        >
                          <Iconify icon="material-symbols:close-small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                >
                  {[...unselectedTransactions, transactions.find((t) => t.id === sellerRecordingFeeTransaction)]
                    .filter((a) => !!a)
                    .map((transaction) => (
                      <MenuItem key={transaction!.id} value={transaction!.id}>
                        {transaction!.name} - ${transaction!.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  disabled={Boolean(buyerTransferTaxTransaction)}
                  fullWidth
                  label="Buyer Transfer Tax"
                  type="number"
                  value={buyerTransferTax}
                  onChange={(e) => setBuyerTransferTax(e.target.value)}
                  InputProps={{
                    startAdornment: "$"
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Choose Pending Transaction"
                  disabled={Boolean(recordingLedgerInfo?.buyerTransferTax)}
                  value={buyerTransferTaxTransaction}
                  onChange={(e) => {
                    const transaction = transactions.find((t) => t.id === e.target.value);
                    setBuyerTransferTax(transaction?.amount as any);
                    setBuyerTransferTaxTransaction(e.target.value);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: buyerTransferTaxTransaction && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setBuyerTransferTaxTransaction(null);
                            setBuyerTransferTax("");
                          }}
                          size="small"
                        >
                          <Iconify icon="material-symbols:close-small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                >
                  {[...unselectedTransactions, transactions.find((t) => t.id === buyerTransferTaxTransaction)]
                    .filter((a) => !!a)
                    .map((transaction) => (
                      <MenuItem key={transaction!.id} value={transaction!.id}>
                        {transaction!.name} - ${transaction!.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  disabled={Boolean(sellerTransferTaxTransaction)}
                  fullWidth
                  label="Seller Transfer Tax"
                  type="number"
                  value={sellerTransferTax}
                  onChange={(e) => setSellerTransferTax(e.target.value)}
                  InputProps={{
                    startAdornment: "$"
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Choose Pending Transaction"
                  disabled={Boolean(recordingLedgerInfo?.sellerTransferTax)}
                  value={sellerTransferTaxTransaction}
                  onChange={(e) => {
                    const transaction = transactions.find((t) => t.id === e.target.value);
                    setSellerTransferTax(transaction?.amount as any);
                    setSellerTransferTaxTransaction(e.target.value);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: sellerTransferTaxTransaction && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setSellerTransferTaxTransaction(null);
                            setSellerTransferTax("");
                          }}
                          size="small"
                        >
                          <Iconify icon="material-symbols:close-small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                >
                  {[...unselectedTransactions, transactions.find((t) => t.id === sellerTransferTaxTransaction)]
                    .filter((a) => !!a)
                    .map((transaction) => (
                      <MenuItem key={transaction!.id} value={transaction!.id}>
                        {transaction!.name} - ${transaction!.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  disabled={Boolean(mortgageTaxTransaction)}
                  fullWidth
                  label="Mortgage Tax"
                  type="number"
                  value={mortgageTax}
                  onChange={(e) => setMortgageTax(e.target.value)}
                  InputProps={{
                    startAdornment: "$"
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Choose Pending Transaction"
                  disabled={Boolean(recordingLedgerInfo?.mortgageTax)}
                  value={mortgageTaxTransaction}
                  onChange={(e) => {
                    const transaction = transactions.find((t) => t.id === e.target.value);
                    setMortgageTax(transaction?.amount as any);
                    setMortgageTaxTransaction(e.target.value);
                    setMortgageTaxTransaction(e.target.value);
                  }}
                  variant="outlined"
                  InputProps={{
                    endAdornment: mortgageTaxTransaction && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setMortgageTaxTransaction(null);
                            setMortgageTax("");
                          }}
                          size="small"
                        >
                          <Iconify icon="material-symbols:close-small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                >
                  {[...unselectedTransactions, transactions.find((t) => t.id === mortgageTaxTransaction)]
                    .filter((a) => !!a)
                    .map((transaction) => (
                      <MenuItem key={transaction!.id} value={transaction!.id}>
                        {transaction!.name} - ${transaction!.amount.toFixed(2)}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            {shouldShowWithholdingTax && (
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={Boolean(withholdingTaxTransaction)}
                    fullWidth
                    label="Withholding Tax"
                    type="number"
                    value={withholdingTax}
                    onChange={(e) => setWithholdingTax(e.target.value)}
                    InputProps={{
                      startAdornment: "$"
                    }}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label="Choose Pending Transaction"
                    disabled={Boolean(recordingLedgerInfo?.withholdingTax)}
                    value={withholdingTaxTransaction}
                    onChange={(e) => {
                      const transaction = transactions.find((t) => t.id === e.target.value);
                      setWithholdingTax(transaction?.amount as any);
                      setWithholdingTaxTransaction(e.target.value);
                    }}
                    variant="outlined"
                    InputProps={{
                      endAdornment: withholdingTaxTransaction && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setWithholdingTaxTransaction(null);
                              setWithholdingTax("");
                            }}
                            size="small"
                          >
                            <Iconify icon="material-symbols:close-small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  >
                    {[...unselectedTransactions, transactions.find((t) => t.id === withholdingTaxTransaction)]
                      .filter((a) => !!a)
                      .map((transaction) => (
                        <MenuItem key={transaction!.id} value={transaction!.id}>
                          {transaction!.name} - ${transaction!.amount.toFixed(2)}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              </Grid>
            )}
          </Grid>
          <UploadMultiFile
            files={file ? [file] : []}
            onDrop={handleFileDrop}
            onRemove={() => setFile(null)}
            onRemoveAll={() => setFile(null)}
            showPreview={false}
            showSelectedFiles={true}
            multiple={false}
            sx={{ marginTop: 3 }}
          />
          {file && (
            <Typography marginTop={3}>
              Uploaded document will be saved to
              <br /> Post Closing {">"} Escrow {">"} Escrow agreement
            </Typography>
          )}

          {transferError && <Typography color="error" sx={{ mt: 2 }}>{transferError}</Typography>}
          {!isValidModalAmount && (
            <Typography marginTop={3} marginBottom={3} sx={{ color: "error.main" }}>
              Enter positive amounts with up to two decimal places. Existing categories cannot be cleared, and the increase cannot exceed the order ledger balance.
            </Typography>
          )}

          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 3, paddingRight: 2 }}>
            <Grid item>
              <Button onClick={onRecordingModalCancelClick} color="error" variant="contained">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={!isValidModalAmount || isRecordingTransferInProgress}
                onClick={onRecordingModalTransferClick}
                color="primary"
                variant="contained"
              >
                {isRecordingTransferInProgress ? <CircularProgress size={24} color="inherit" /> : recordingLedgerInfo ? "Save" : "Transfer"}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClosingChecklistLedgers;
